import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Public Pages (NO AUTH - No ClerkProvider)
import Landing from "@/pages/Landing";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";

// Context Providers
import { TenantProvider } from "@/contexts/TenantContext";
import { DashboardDataProvider } from "@/contexts/DashboardDataContext";

// Auth Layout & Pages (ClerkProvider wrapped)
import AuthLayout from "@/layouts/AuthLayout";
import AdminLayout from "@/layouts/AdminLayout";
import AppLayout from "@/layouts/AppLayout";
import CWLayout from "@/layouts/CWLayout";
import Dashboard from "@/pages/Dashboard";

// UI Harness (NO CLERK, NO AUTH - Testing only)
import UIHarnessIndex from "@/harness/UIHarnessIndex";
import UIHarnessWrapper from "@/harness/UIHarnessWrapper";
import { HarnessDemoPage } from "@/harness/HarnessDemoPage";
import HarnessAppLayout from "@/harness/HarnessAppLayout";
import HarnessAdminLayout from "@/harness/HarnessAdminLayout";
import HarnessCWLayout from "@/harness/HarnessCWLayout";

import DashboardLayout from "@/layouts/DashboardLayout";
import DashboardOverview from "@/pages/DashboardOverview";
import CWDashboard from "@/pages/CWDashboard";
import CustomersPage from "@/pages/CustomersPage";
import CampaignsPage from "@/pages/CampaignsPage";
import WalletPage from "@/pages/WalletPage";
import SettingsPage from "@/pages/SettingsPage";

// Phase 5 Pages
import AnalyticsPage from "@/pages/AnalyticsPage";
import TeamPage from "@/pages/TeamPage";
import SegmentsPage from "@/pages/SegmentsPage";
import TemplatesPage from "@/pages/TemplatesPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSmsPacksPage from "@/pages/AdminSmsPacksPage";
import AdminPromoCodesPage from "@/pages/AdminPromoCodesPage";
import CreateCampaignPage from "@/pages/CreateCampaignPage";
import CampaignDetailPage from "@/pages/CampaignDetailPage";
import SmsHistoryPage from "@/pages/SmsHistoryPage";

import IntegrationsPage from "@/pages/IntegrationsPage";
import ReviewsPage from "@/pages/ReviewsPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";

// Super Admin wrapper component with TenantProvider
function SuperAdminPage() {
  return (
    <TenantProvider>
      <div className="min-h-screen bg-gray-50">
        <AdminDashboard />
      </div>
    </TenantProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* ============================================ */}
        {/* UI HARNESS ROUTES - NO AUTH, NO CLERK       */}
        {/* For visual testing only - safe to delete    */}
        {/* ============================================ */}
        
        {/* Harness Index */}
        <Route path="/__ui-harness" element={<UIHarnessIndex />} />
        
        {/* AppLayout Harness Routes */}
        <Route path="/__ui-harness/dashboard" element={
          <UIHarnessWrapper layout="app">
            <HarnessAppLayout>
              <HarnessDemoPage 
                title="Dashboard Overview" 
                description="AppLayout demo with orange theme"
              />
            </HarnessAppLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/wallet" element={
          <UIHarnessWrapper layout="app">
            <HarnessAppLayout>
              <HarnessDemoPage 
                title="Wallet" 
                description="SMS credits and balance management"
              />
            </HarnessAppLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/campaigns" element={
          <UIHarnessWrapper layout="app">
            <HarnessAppLayout>
              <HarnessDemoPage 
                title="Campaigns" 
                description="SMS campaign management"
              />
            </HarnessAppLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/analytics" element={
          <UIHarnessWrapper layout="app">
            <HarnessAppLayout>
              <HarnessDemoPage 
                title="Analytics" 
                description="Campaign performance and ROI"
              />
            </HarnessAppLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/customers" element={
          <UIHarnessWrapper layout="app">
            <HarnessAppLayout>
              <HarnessDemoPage 
                title="Customers" 
                description="Customer database management"
              />
            </HarnessAppLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/templates" element={
          <UIHarnessWrapper layout="app">
            <HarnessAppLayout>
              <HarnessDemoPage 
                title="Templates" 
                description="SMS message templates"
              />
            </HarnessAppLayout>
          </UIHarnessWrapper>
        } />
        
        {/* AdminLayout Harness Routes */}
        <Route path="/__ui-harness/admin/sms-packs" element={
          <UIHarnessWrapper layout="admin">
            <HarnessAdminLayout>
              <HarnessDemoPage 
                title="SMS Packs Management" 
                description="AdminLayout demo with purple theme"
              />
            </HarnessAdminLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/admin/promo-codes" element={
          <UIHarnessWrapper layout="admin">
            <HarnessAdminLayout>
              <HarnessDemoPage 
                title="Promo Codes Management" 
                description="Create and manage promotional codes"
              />
            </HarnessAdminLayout>
          </UIHarnessWrapper>
        } />
        
        {/* CWLayout Harness Routes */}
        <Route path="/__ui-harness/cw/dashboard" element={
          <UIHarnessWrapper layout="cw">
            <HarnessCWLayout>
              <HarnessDemoPage 
                title="Dashboard (CW Style)" 
                description="CloudWaitress-inspired layout demo"
              />
            </HarnessCWLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/cw/wallet" element={
          <UIHarnessWrapper layout="cw">
            <HarnessCWLayout>
              <HarnessDemoPage 
                title="Wallet (CW Style)" 
                description="CloudWaitress-inspired wallet page"
              />
            </HarnessCWLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/cw/campaigns" element={
          <UIHarnessWrapper layout="cw">
            <HarnessCWLayout>
              <HarnessDemoPage 
                title="Campaigns (CW Style)" 
                description="CloudWaitress-inspired campaigns page"
              />
            </HarnessCWLayout>
          </UIHarnessWrapper>
        } />
        <Route path="/__ui-harness/cw/analytics" element={
          <UIHarnessWrapper layout="cw">
            <HarnessCWLayout>
              <HarnessDemoPage 
                title="Analytics (CW Style)" 
                description="CloudWaitress-inspired analytics page"
              />
            </HarnessCWLayout>
          </UIHarnessWrapper>
        } />

        {/* PUBLIC ROUTES – NO AUTH WRAPPER, NO BACKEND CALLS */}
        <Route path="/" element={<Landing />} />
        <Route path="/sign-in/*" element={<SignIn />} />
        <Route path="/sign-up/*" element={<SignUp />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        
        {/* AUTHENTICATED ROUTES – ClerkProvider wrapped via AuthLayout */}
        <Route element={<AuthLayout />}>
          {/* Super Admin Route - hidden URL */}
          <Route path="/super-admin-raj-singh" element={<SuperAdminPage />} />
          <Route path="/super-admin-raj-singh/sms-packs" element={
            <TenantProvider>
              <AdminLayout>
                <AdminSmsPacksPage />
              </AdminLayout>
            </TenantProvider>
          } />
          <Route path="/super-admin-raj-singh/promo-codes" element={
            <TenantProvider>
              <AdminLayout>
                <AdminPromoCodesPage />
              </AdminLayout>
            </TenantProvider>
          } />
          
          {/* CLOUDWAITRESS UI TEST ROUTES - Admin-only, test-only, not linked in UI */}
          {/* These routes are safe to delete and are for visual comparison only */}
          <Route path="/__ui-test/cloudwaitress/wallet" element={
            <TenantProvider>
              <DashboardDataProvider>
                <CWLayout>
                  <WalletPage />
                </CWLayout>
              </DashboardDataProvider>
            </TenantProvider>
          } />
          <Route path="/__ui-test/cloudwaitress/campaigns" element={
            <TenantProvider>
              <DashboardDataProvider>
                <CWLayout>
                  <CampaignsPage />
                </CWLayout>
              </DashboardDataProvider>
            </TenantProvider>
          } />
          <Route path="/__ui-test/cloudwaitress/analytics" element={
            <TenantProvider>
              <DashboardDataProvider>
                <CWLayout>
                  <AnalyticsPage />
                </CWLayout>
              </DashboardDataProvider>
            </TenantProvider>
          } />
          <Route path="/__ui-test/cloudwaitress/dashboard" element={
            <TenantProvider>
              <DashboardDataProvider>
                <CWLayout>
                  <CWDashboard />
                </CWLayout>
              </DashboardDataProvider>
            </TenantProvider>
          } />
          
          {/* Dashboard with onboarding check */}
          <Route path="/dashboard" element={<Dashboard />}>
            {/* Nested routes inside DashboardLayout */}
            <Route index element={<DashboardOverview />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="campaigns/new" element={<CreateCampaignPage />} />
            <Route path="campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="history" element={<SmsHistoryPage />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Phase 5 Routes */}
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="segments" element={<SegmentsPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="admin" element={<AdminDashboard />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="reviews" element={<ReviewsPage />} />
          </Route>
          
          {/* Catch-all redirect for unmatched authenticated routes */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
